import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Table } from 'reactstrap';
import { debounce } from 'lodash';
import moment from 'moment';
import RefreshIcon from '@material-ui/icons/Refresh';
import { DownloadReport, GetReportDataUserWise } from './myReportsController';

// Add CSS for the spinning animation
const spinAnimation = `
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
`;

const MyReportsGeneratedData = forwardRef(({ activeTab, displayAlertMessage }, ref) => {
    // Initial state for the table data
    const [tableData, setTableData] = useState([]);
    const [jobList, setJobList] = useState([]);
    // State for pagination, sorting and filtering
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        totalItems: 0
    });

    // Page size options
    const pageSizeOptions = [5, 10, 25, 50];

    // Handle page size change
    const handlePageSizeChange = (size) => {
        setPagination(prev => ({
            ...prev,
            pageSize: size,
            currentPage: 1 // Reset to first page when changing page size
        }));
    };

    // State for sorting options
    const [sortInfo, setSortInfo] = useState({
        column: "jobId",
        direction: "desc" // Most recent jobs first
    });

    // State for loading and error
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // State for auto-refresh interval
    const [refreshInterval, setRefreshInterval] = useState(null);
    // State for tracking which reports are currently being downloaded
    const [downloadingReports, setDownloadingReports] = useState({});

    // Fetch data from API - mocked for now
    const fetchData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 500));

            var obj = JSON.parse(localStorage.getItem("authUser"))
            var user = obj[2]
            var location = obj[3]

            const response = await GetReportDataUserWise(user, location);

            if (!response.success) {
                setJobList([]);
                setTableData([]);
                setPagination(prev => ({ ...prev, totalItems: 0 }));
                return;
            }

            // Store the full data list for processing status checks
            setJobList(response.data);

            // Use response data directly without filtering
            let filteredData = [...response.data];

            // Apply pagination
            const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
            const paginatedData = filteredData.slice(startIndex, startIndex + pagination.pageSize);

            // Update table data state
            setTableData(paginatedData);
            setPagination(prev => ({ ...prev, totalItems: filteredData.length }));
        } catch (err) {
            setError("Failed to fetch report data: " + (err.message || "Unknown error"));
        } finally {
            setIsLoading(false);
        }
    };

    // Check if there are any jobs that need monitoring (Processing, Pending, or Queued)
    const hasProcessingJobs = () => {
        // Check all data, not just the current page
        return jobList.some(item =>
            item.status === "Processing" ||
            item.status === "Pending" ||
            item.status === "Queued"
        );
    };

    // Initialize or stop auto-refresh based on tab and processing jobs
    useEffect(() => {
        console.log("Tab changed or job list updated, activeTab:", activeTab);
        console.log("Has processing jobs:", hasProcessingJobs());

        // If we're on the Requested Reports tab and there are processing jobs
        if (activeTab === "2" && hasProcessingJobs()) {
            // Auto-refresh every 10 seconds if there are processing jobs
            console.log("Setting refresh interval to 10000ms");
            setRefreshInterval(30000);
        } else {
            // Stop auto-refresh if we're not on Requested Reports tab
            console.log("Clearing refresh interval");
            setRefreshInterval(null);
        }

        // Cleanup on unmount
        return () => setRefreshInterval(null);
    }, [activeTab, jobList]);

    // Effect to fetch data when component mounts
    useEffect(() => {
        fetchData();
    }, []);

    // Effect for auto-refresh mechanism
    useEffect(() => {
        let intervalId = null;

        if (refreshInterval) {
            intervalId = setInterval(() => {
                fetchData();
            }, refreshInterval);
        }

        // Cleanup function to clear interval when component unmounts or dependencies change
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [refreshInterval]);

    // Handle column header click for sorting
    const handleSortClick = (column) => {
        setSortInfo(prev => ({
            column,
            direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
        }));
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, currentPage: newPage }));
    };

    // Calculate total pages
    const totalPages = Math.ceil(pagination.totalItems / pagination.pageSize);

    // Handle download and track loading state
    const handleDownload = async (jobId, pdfName) => {
        try {
            // Set downloading state for this specific report
            setDownloadingReports(prev => ({ ...prev, [jobId]: true }));

            // Create a promise for the download and a timeout
            const downloadPromise = DownloadReport(jobId, pdfName);
            const delay = new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay

            // Wait for both download and delay to complete
            await Promise.all([downloadPromise, delay]);

            if (!downloadPromise) {
                displayAlertMessage("danger", "Download failed. Please try again.");
            }

        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            // Reset downloading state when complete
            setDownloadingReports(prev => ({ ...prev, [jobId]: false }));
        }
    };

    // Export methods via ref
    useImperativeHandle(ref, () => ({
        refresh: fetchData,
        exportData: () => {
            // Logic to export data to CSV or PDF would go here
            console.log("Exporting data...");
            return tableData;
        },
        resetFilters: () => {
            console.log("Reset filters functionality is removed.");
        },
        setAutoRefresh: (interval) => {
            setRefreshInterval(interval);
        }
    }));

    // Render component
    return (
        <React.Fragment>
            {/* Add the CSS for spin animation */}
            <style>
                {`
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                    .spin-animation {
                        animation: spin 1s linear infinite;
                    }
                `}
            </style>

            {/* Loading and Error Messages */}
            {error && <div className="alert alert-danger my-3">{error}</div>}

            {/* Table Section */}
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>Generated Reports</div>
                        <button
                            className="btn btn-sm btn-outline-primary d-flex align-items-center"
                            onClick={fetchData}
                            disabled={isLoading}
                        >
                            <RefreshIcon style={{ fontSize: '1rem', marginRight: '4px' }}
                                className={isLoading ? 'spin-animation' : ''}
                            />
                            {isLoading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </CardHeader>
                <CardBody>
                    <Table responsive striped bordered hover>
                        <thead>
                            <tr>
                                <th onClick={() => handleSortClick("jobId")} style={{ cursor: "pointer" }}>
                                    Job ID {sortInfo.column === "jobId" && (sortInfo.direction === "asc" ? "↑" : "↓")}
                                </th>
                                <th onClick={() => handleSortClick("reportName")} style={{ cursor: "pointer" }}>
                                    Report Name {sortInfo.column === "reportName" && (sortInfo.direction === "asc" ? "↑" : "↓")}
                                </th>
                                <th onClick={() => handleSortClick("requestedAt")} style={{ cursor: "pointer" }}>
                                    Requested At {sortInfo.column === "requestedAt" && (sortInfo.direction === "asc" ? "↑" : "↓")}
                                </th>
                                <th onClick={() => handleSortClick("generatedAt")} style={{ cursor: "pointer" }}>
                                    Generated At {sortInfo.column === "generatedAt" && (sortInfo.direction === "asc" ? "↑" : "↓")}
                                </th>
                                <th onClick={() => handleSortClick("status")} style={{ cursor: "pointer" }}>
                                    Status {sortInfo.column === "status" && (sortInfo.direction === "asc" ? "↑" : "↓")}
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center">No reports found</td>
                                </tr>
                            ) : (
                                tableData.map((row, index) => (
                                    <tr key={row.jobId}>
                                        <td>{row.jobId}</td>
                                        <td>{row.reportName}</td>
                                        <td>{row.requestedAt ? moment(row.requestedAt).format("MMM DD, YYYY hh:mm A") : "-"}</td>
                                        <td>{row.generatedAt ? moment(row.generatedAt).format("MMM DD, YYYY hh:mm A") : "-"}</td>
                                        <td>
                                            <div style={{
                                                backgroundColor: row.status === "Completed" ? "#42d29d" :
                                                    row.status === "Processing" ? "#f7cc53" :
                                                        row.status === "Pending" ? "#50a5f1" :
                                                            row.status === "Queued" ? "#74788d" : "#f06548",
                                                color: "white",
                                                padding: "5px 12px",
                                                borderRadius: "4px",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minWidth: "120px"
                                            }}>
                                                {row.status === "Completed" && (
                                                    <i className="bx bx-check-circle me-1" style={{ fontSize: "16px" }}></i>
                                                )}
                                                {row.status === "Failed" && (
                                                    <i className="bx bx-x-circle me-1" style={{ fontSize: "16px" }}></i>
                                                )}
                                                {row.status === "Processing" && (
                                                    <i className="bx bx-loader bx-spin me-1" style={{ fontSize: "16px" }}></i>
                                                )}
                                                {row.status === "Pending" && (
                                                    <i className="bx bx-time me-1" style={{ fontSize: "16px" }}></i>
                                                )}
                                                {row.status === "Queued" && (
                                                    <i className="bx bx-list-ul me-1" style={{ fontSize: "16px" }}></i>
                                                )}
                                                {row.status}
                                            </div>
                                        </td>
                                        <td>
                                            {row.status === "Completed" && (
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleDownload(row.jobId, row.pdfName)}
                                                    disabled={downloadingReports[row.jobId]}
                                                >
                                                    {downloadingReports[row.jobId] ? (
                                                        <>
                                                            <i className="bx bx-loader bx-spin me-1" style={{ fontSize: "16px" }}></i>
                                                            Downloading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bx bx-download"></i> Download
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    {/* Pagination */}
                    {tableData.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                Showing {Math.min((pagination.currentPage - 1) * pagination.pageSize + 1, pagination.totalItems)} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems} entries
                            </div>
                            <div>
                                <button
                                    className="btn btn-sm btn-outline-primary me-2"
                                    disabled={pagination.currentPage === 1}
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages).keys()].map(page => (
                                    <button
                                        key={page + 1}
                                        className={`btn btn-sm ${pagination.currentPage === page + 1 ? 'btn-primary' : 'btn-outline-primary'} me-2`}
                                        onClick={() => handlePageChange(page + 1)}
                                    >
                                        {page + 1}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={pagination.currentPage === totalPages}
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                >
                                    Next
                                </button>
                            </div>
                            <div>
                                <select
                                    className="form-select form-select-sm"
                                    value={pagination.pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                >
                                    {pageSizeOptions.map(size => (
                                        <option key={size} value={size}>{size} per page</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        </React.Fragment>
    );
});

export default MyReportsGeneratedData;
